// Shared plumbing for the details.autospy.ee proxy endpoints.
//
// In dev every path is proxied by Vite (see vite.config.ts) so the browser
// stays same-origin; in production the endpoints are called directly.

export class ApiError extends Error {
  readonly statusCode: number | null;

  constructor(message: string, statusCode: number | null = null) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
  }
}

const PROD_DETAILS_BASE = 'https://details.autospy.ee';

/** Token for /car-data, /car-tax and /auto24-price; injected at build time, never hardcoded. */
export function detailsApiToken(): string {
  return import.meta.env.VITE_DETAILS_API_TOKEN?.trim() ?? '';
}

export function buildDetailsUrl(path: string, params: Record<string, string>): string {
  const base = import.meta.env.VITE_DETAILS_API_BASE?.trim();
  const endpoint = base && base.length > 0 ? `${base.replace(/\/$/, '')}${path}` : import.meta.env.DEV ? path : `${PROD_DETAILS_BASE}${path}`;
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  const url = new URL(endpoint, origin);
  for (const [key, value] of Object.entries(params)) {
    if (value) url.searchParams.set(key, value);
  }
  return url.toString();
}

export interface FetchJsonOptions {
  signal?: AbortSignal | undefined;
  timeoutMs: number;
  /** Prefix for error messages, e.g. "Car data". */
  label: string;
  init?: RequestInit;
}

export async function fetchJson<T>(url: string, options: FetchJsonOptions): Promise<T> {
  const controller = new AbortController();
  const timeout = globalThis.setTimeout(() => controller.abort(), options.timeoutMs);
  const abortFromCaller = () => controller.abort();
  options.signal?.addEventListener('abort', abortFromCaller, { once: true });

  try {
    const res = await fetch(url, { ...options.init, signal: controller.signal });
    if (!res.ok) throw new ApiError(`${options.label}: HTTP ${res.status}`, res.status);
    const text = await res.text();
    if (!text.trim()) throw new ApiError(`${options.label}: empty response`, res.status);
    try {
      return JSON.parse(text) as T;
    } catch {
      throw new ApiError(`${options.label}: invalid JSON (${text.slice(0, 100)})`, res.status);
    }
  } catch (err) {
    if (err instanceof ApiError) throw err;
    if (err instanceof DOMException && err.name === 'AbortError') {
      if (options.signal?.aborted) throw new ApiError(`${options.label}: cancelled`, null);
      throw new ApiError(`${options.label}: request timed out`, 504);
    }
    throw new ApiError(`${options.label}: ${err instanceof Error ? err.message : String(err)}`, null);
  } finally {
    globalThis.clearTimeout(timeout);
    options.signal?.removeEventListener('abort', abortFromCaller);
  }
}
